import { KeyRound, Mail, Pencil, Plus, UserSquare2 } from "lucide-react";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  InfoBanner,
  Input,
  Modal,
  Spinner,
} from "@/components/ui";
import { ImageField } from "@/components/ImageField";
import { toApiError } from "@/lib/api";
import { useT, type Chave } from "@/lib/i18n";
import { cpfMask, isValidCpf } from "@/lib/format";
import {
  useProfessionalAccess,
  useProfessionalCrud,
  useProfessionalPhoto,
  useProfessionals,
  useServices,
} from "@/lib/queries";
import type { AccessStatus, Me, Professional } from "@/lib/types";

const ACCESS_BADGE: Record<AccessStatus, { label: Chave; className: string }> = {
  active: {
    label: "team.withLogin",
    className: "border-emerald-800/50 bg-emerald-950/50 text-emerald-300",
  },
  invited: {
    label: "team.inviteSent",
    className: "border-sky-800/50 bg-sky-950/50 text-sky-300",
  },
  none: {
    label: "team.noLogin",
    className: "border-amber-700/50 bg-amber-950/50 text-amber-300",
  },
};

function AccessBadge({ status }: { status: AccessStatus }) {
  const t = useT();
  const { label, className } = ACCESS_BADGE[status];
  return <Badge className={className}>{t(label)}</Badge>;
}

export default function Team() {
  const t = useT();
  const me = useOutletContext<Me>();
  const { data: professionals, isLoading } = useProfessionals();
  const [editing, setEditing] = useState<Professional | null>(null);
  const [creating, setCreating] = useState(false);
  const [managingAccess, setManagingAccess] = useState<Professional | null>(null);

  const active = professionals?.filter((p) => p.is_active).length ?? 0;
  const limit = me.garage.plan?.max_professionals ?? 0;
  const atLimit = active >= limit;

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold lg:text-2xl">{t("nav.team")}</h1>
          <p className="text-sm text-steel-muted">
            {active} de {limit} profissionais do plano {me.garage.plan?.name}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus size={18} />
          {t("team.newShort")}
        </Button>
      </div>

      {atLimit && (
        // Limite de plano é oportunidade de venda, não obstáculo.
        <Card className="mb-4 border-amber-800/50 bg-amber-950/40 p-4">
          <p className="text-sm text-amber-300">
            <span
              dangerouslySetInnerHTML={{
                __html: t("team.limitText", { n: limit, plan: me.garage.plan?.name ?? "" }),
              }}
            />
          </p>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      ) : professionals?.length === 0 ? (
        <Card>
          <EmptyState
            icon={<UserSquare2 size={40} />}
            title={t("team.emptyTitle")}
            description={t("team.emptyText")}
            action={<Button onClick={() => setCreating(true)}>{t("team.addFirst")}</Button>}
          />
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {professionals?.map((p) => (
            <Card key={p.id} className="flex items-center gap-3 p-4">
              {/* Sem foto, a cor da agenda vira o avatar: é o mesmo sinal
                  que identifica o profissional nos horários. */}
              {p.photo ? (
                <img
                  src={p.photo}
                  alt=""
                  className="size-10 shrink-0 rounded-full border border-line object-cover"
                />
              ) : (
                <div
                  className="size-10 shrink-0 rounded-full"
                  style={{ backgroundColor: p.color }}
                  aria-hidden
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium">{p.display_name}</span>
                  {!p.is_active && (
                    <Badge className="bg-carbon-700 text-steel-dim">{t("common.inactive")}</Badge>
                  )}
                  <AccessBadge status={p.access_status} />
                </div>
                <p className="truncate text-sm text-steel-muted">
                  {p.services.filter((s) => s.is_active).length} serviço(s) ·{" "}
                  {p.commission_percent}% de comissão
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setManagingAccess(p)}
                title={t("team.dashboardAccess")}
              >
                <KeyRound size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                <Pencil size={16} />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <ProfessionalForm
        open={creating || editing !== null}
        professional={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />
      <AccessModal
        professional={managingAccess}
        onClose={() => setManagingAccess(null)}
      />
    </div>
  );
}

function ProfessionalForm({
  open,
  professional,
  onClose,
}: {
  open: boolean;
  professional: Professional | null;
  onClose: () => void;
}) {
  const t = useT();
  const { region } = useOutletContext<Me>().garage;
  const me = useOutletContext<Me>();
  const { create, update } = useProfessionalCrud();
  const enviarFoto = useProfessionalPhoto();
  const { data: services } = useServices();
  const [name, setName] = useState("");
  const [commission, setCommission] = useState("50");
  const [color, setColor] = useState("#2563eb");
  const [bio, setBio] = useState("");
  const [cpf, setCpf] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [loaded, setLoaded] = useState<number | null>(null);

  if (open && professional && loaded !== professional.id) {
    setName(professional.display_name);
    setCommission(professional.commission_percent);
    setColor(professional.color);
    setBio(professional.bio);
    setCpf(cpfMask(professional.document));
    setPhoto(null);
    setServiceIds(
      professional.services.filter((s) => s.is_active).map((s) => s.service),
    );
    setLoaded(professional.id);
  }
  if (open && !professional && loaded !== null) {
    setName("");
    setCommission("50");
    setColor("#2563eb");
    setBio("");
    setCpf("");
    setPhoto(null);
    setServiceIds([]);
    setLoaded(null);
  }

  const cpfInvalido = cpf.replace(/\D/g, "").length > 0 && !isValidCpf(cpf);

  const mutation = professional ? update : create;
  const error = mutation.isError
    ? toApiError(mutation.error)
    : enviarFoto.isError
      ? toApiError(enviarFoto.error)
      : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (cpfInvalido) return;
    const body = {
      display_name: name,
      color,
      bio,
      document: cpf.replace(/\D/g, ""),
      service_ids: serviceIds,
      // A comissão só entra no corpo quando quem edita é o dono. O
      // backend recusa a alteração de qualquer forma, mas mandar o campo
      // faria o gerente levar um 403 ao salvar só a foto de alguém.
      ...(me.permissions.edit_commission ? { commission_percent: commission } : {}),
    };
    mutation.mutate(
      (professional ? { id: professional.id, ...body } : body) as never,
      {
        // A foto sobe num segundo request, depois de existir o id: um
        // profissional novo só ganha id no retorno do POST. Fazer tudo
        // num multipart só exigiria mandar service_ids como campo
        // repetido de formulário, e o serializer espera lista JSON.
        onSuccess: (salvo: Professional) => {
          const terminar = () => {
            setLoaded(null);
            onClose();
          };
          if (photo) {
            enviarFoto.mutate({ id: salvo.id, photo }, { onSuccess: terminar });
          } else {
            terminar();
          }
        },
      },
    );
  };

  const enviando = mutation.isPending || enviarFoto.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={professional ? t("team.edit") : t("team.new")}
    >
      <form onSubmit={submit} className="space-y-4">
        <Input
          label={t("common.name")}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t("team.commission")}
            type="number"
            min={0}
            max={100}
            step="0.5"
            value={commission}
            disabled={!me.permissions.edit_commission}
            onChange={(e) => setCommission(e.target.value)}
            hint={
              me.permissions.edit_commission
                ? undefined
                : t("team.commissionLocked")
            }
          />
          <Input
            label={t("team.agendaColor")}
            type="color"
            className="h-10 p-1"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </div>

        {/* Só onde existe repasse para fazer. O campo é CPF e serve para
            pagar comissão por Pix — nos EUA não há nem o documento nem o
            meio de pagamento, e pedir o documento pessoal de um mecânico
            para não usar em nada é o oposto de pedir o mínimo. */}
        {region.payout_method === "pix" && (
          <Input
            label="CPF"
            inputMode="numeric"
            placeholder="000.000.000-00"
            value={cpf}
            onChange={(e) => setCpf(cpfMask(e.target.value))}
            error={
              cpfInvalido ? t("team.taxIdInvalid", { documento: "CPF" }) : undefined
            }
            hint={cpfInvalido ? undefined : t("team.taxIdHint")}
          />
        )}

        <ImageField
          label={t("team.photo")}
          hint={t("team.photoHint")}
          shape="circle"
          value={professional?.photo ?? null}
          file={photo}
          onPick={setPhoto}
        />

        <label className="block">
          <span className="eyebrow mb-1.5 block text-steel-dim">
            {t("profile.bio")} <span className="normal-case tracking-normal">({t("common.optional")})</span>
          </span>
          <textarea
            rows={2}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder={t("team.bioPlaceholder")}
            className="w-full rounded-[4px] border border-line bg-carbon-900 px-3 py-2.5 text-steel outline-none transition placeholder:text-steel-dim focus:border-nitro-500"
          />
        </label>

        <div>
          <p className="mb-2 text-sm font-medium text-steel">
            {t("team.servicesPerformed")}
          </p>
          <div className="max-h-52 space-y-1 overflow-y-auto rounded-[4px] border border-line p-2">
            {services
              ?.filter((s) => s.is_active)
              .map((s) => (
                <label
                  key={s.id}
                  className="flex cursor-pointer items-center gap-2 rounded p-2 text-sm hover:bg-carbon-900"
                >
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={serviceIds.includes(s.id)}
                    onChange={(e) =>
                      setServiceIds((prev) =>
                        e.target.checked
                          ? [...prev, s.id]
                          : prev.filter((id) => id !== s.id),
                      )
                    }
                  />
                  {s.name}
                </label>
              ))}
          </div>
          <p className="mt-1 text-xs text-steel-muted">
            {t("team.needOneService")}
          </p>
        </div>

        {/* `code` junto: cadastrar profissional é onde o teto do plano
            aparece (`check_professional_limit`), e é aí que a negativa
            precisa virar convite para upgrade em vez de erro seco. */}
        <ErrorBanner message={error?.message} code={error?.code} />

        <Button type="submit" className="w-full" disabled={enviando || cpfInvalido}>
          {enviando ? (
            <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
          ) : (
            t("common.save")
          )}
        </Button>
      </form>
    </Modal>
  );
}

/**
 * Acesso ao painel: convite por e-mail ou senha provisória na mão.
 *
 * As duas portas existem porque profissional que não abre e-mail é regra, não
 * exceção — e um cadastro que depende do e-mail chegar trava no primeiro
 * profissional. A senha provisória aparece uma única vez, e o
 * colaborador é obrigado a trocá-la no primeiro acesso.
 */
function AccessModal({
  professional,
  onClose,
}: {
  professional: Professional | null;
  onClose: () => void;
}) {
  const t = useT();
  const me = useOutletContext<Me>();
  const { invite, setPassword, revoke } = useProfessionalAccess();
  const [email, setEmail] = useState("");
  const [loaded, setLoaded] = useState<number | null>(null);
  const [senhaGerada, setSenhaGerada] = useState<string | null>(null);
  const [linkConvite, setLinkConvite] = useState<string | null>(null);

  if (professional && loaded !== professional.id) {
    setEmail(professional.user_email ?? "");
    setSenhaGerada(null);
    setLinkConvite(null);
    setLoaded(professional.id);
  }

  const fechar = () => {
    setLoaded(null);
    onClose();
  };

  const erro = invite.isError
    ? toApiError(invite.error)
    : setPassword.isError
      ? toApiError(setPassword.error)
      : revoke.isError
        ? toApiError(revoke.error)
        : null;
  const enviando = invite.isPending || setPassword.isPending || revoke.isPending;
  const temLogin = professional?.access_status === "active";

  return (
    <Modal
      open={professional !== null}
      onClose={fechar}
      title={t("team.accessOf", { name: professional?.display_name ?? "" })}
    >
      {temLogin ? (
        <div className="space-y-4">
          <p className="text-sm text-steel-muted">
            {t("team.signsInWith")} <b className="text-steel">{professional?.user_email}</b>{" "}
            {t("team.seesOwnAgenda")}
          </p>
          <InfoBanner tone="neutral">
            {t("team.revokeNote")}
          </InfoBanner>
          <ErrorBanner message={erro?.message} />
          <Button
            variant="danger"
            className="w-full"
            disabled={enviando}
            onClick={() => revoke.mutate(professional!.id, { onSuccess: fechar })}
          >
            {enviando ? <Spinner className="size-4" /> : t("team.revokeAccess")}
          </Button>
        </div>
      ) : senhaGerada !== null ? (
        <div className="space-y-4">
          <p className="text-sm text-steel-muted">
            {t("team.credentialsReady")}{" "}
            <b className="text-steel">{t("team.shownOnce")}</b>
            {t("team.mustChange")}
          </p>
          <div className="rounded-[4px] border border-line bg-carbon-900 p-4 text-center">
            <p className="text-sm text-steel-muted">{email}</p>
            <p className="mt-1 font-mono text-2xl tracking-widest text-racing-400">{senhaGerada}</p>
          </div>
          <Button className="w-full" onClick={fechar}>
            {t("team.gotIt")}
          </Button>
        </div>
      ) : linkConvite !== null ? (
        <div className="space-y-4">
          <p className="text-sm text-steel-muted">
            {t("team.inviteSentTo")} <b className="text-steel">{email}</b>
            {t("team.inviteLinkHint", { channel: me.garage.region.messaging_label })}
          </p>
          <div className="break-all rounded-[4px] border border-line bg-carbon-900 p-3 font-mono text-xs text-steel-muted">
            {linkConvite}
          </div>
          <Button className="w-full" onClick={fechar}>
            {t("team.close")}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-steel-muted">
            {t("team.loginExplains")}
          </p>
          <Input
            label={t("team.theirEmail")}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={erro?.field === "email" ? erro.message : undefined}
          />
          {erro && erro.field !== "email" && <ErrorBanner message={erro.message} />}

          <div className="space-y-2">
            <Button
              className="w-full"
              disabled={enviando || !email}
              onClick={() =>
                invite.mutate(
                  { id: professional!.id, email },
                  { onSuccess: (d) => setLinkConvite(d.accept_url) },
                )
              }
            >
              {enviando ? (
                <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
              ) : (
                <>
                  <Mail size={16} /> {t("team.sendInvite")}
                </>
              )}
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              disabled={enviando || !email}
              onClick={() =>
                setPassword.mutate(
                  { id: professional!.id, email },
                  { onSuccess: (d) => setSenhaGerada(d.temp_password) },
                )
              }
            >
              <KeyRound size={16} /> {t("team.generatePassword")}
            </Button>
          </div>
          <p className="text-xs text-steel-dim">
            {t("team.handPasswordHint")}
          </p>
        </div>
      )}
    </Modal>
  );
}
